import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { sitesApi } from './api'

/**
 * The company's places, loaded once and shared.
 *
 * Three modules ask the same question — which plant is this machine in, which
 * plant is this order for, which plant does this person work at — and each of
 * them wants a list to draw a selector with. A store rather than a fetch per
 * screen because the answer is the same on all of them and it is tens of rows,
 * not thousands.
 *
 * `UiReferenceField type="site"` is still the right thing for a form field that
 * points AT one: it searches, it degrades on an instance where nobody provides
 * sites, and it never learns who answered. This is for the other case — a
 * filter that has to draw every option at once, and a screen that has to decide
 * whether to show the column at all.
 */
export const useSitesStore = defineStore('sites', () => {
  const sites = ref([])
  const loading = ref(false)
  const loaded = ref(false)

  /**
   * Whether this instance is a group at all.
   *
   * The one branch every screen takes: with a single site, "where" is not a
   * question, and a filter with one option or a column with one repeated value
   * is furniture. Two is where it starts being worth asking.
   */
  const isMultiSite = computed(() => sites.value.length > 1)

  const byId = (id) => sites.value.find(site => site.id === id) ?? null

  /**
   * @param {boolean} [force] reload even if it is already in memory
   *
   * A failure leaves the list empty on purpose rather than throwing: the caller
   * is a filter on a screen about something else, and a red toast there would
   * be about a field the user never touched.
   */
  async function load(force = false) {
    if (loaded.value && !force) return sites.value
    loading.value = true
    try {
      const data = await sitesApi.list({ active: true })
      sites.value = data.items ?? []
      loaded.value = true
    } catch {
      sites.value = []
    } finally {
      loading.value = false
    }
    return sites.value
  }

  return { sites, loading, loaded, isMultiSite, byId, load }
})
